// src/contexts/BrandingContext.js
//
// Loads settings/branding from Firestore once at app start and exposes a merged
// palette via the useBranding() hook. Caches in localStorage so subsequent
// page loads render instantly without a flash of unstyled content.
//
// Usage:
//   const branding = useBranding();
//   <Box sx={{ backgroundColor: branding.chromeBg }}>...</Box>

import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const STORAGE_KEY = 'tbv_branding_cache_v1';

// Defaults match the legacy hardcoded values in Layout.js. Existing settings/branding
// docs only have primaryColor / secondaryColor / accentColor — the rest derive from
// those or fall back to these defaults.
const DEFAULTS = {
  // Identity
  clubName: 'Torch Beach Volleyball',
  tagline: '',
  logoUrl: '',
  faviconUrl: '',

  // Where the club plays. Separate from the club's own name because they are
  // different things that happen to belong to the same organisation: members
  // say "Corona Beach" for the place and "Torch" for the club, and a club that
  // moves courts changes one without changing the other.
  facilityName: 'Corona Beach',
  facilityAddress: '2020 South Dean Road, Orlando, FL 32825',

  // Sidebar / app bar / drawer
  chromeBg: '#1E3A5F',
  chromeAccent: '#C9A84C',
  chromeAccentMuted: 'rgba(201,168,76,0.6)',
  chromeAccentFaint: 'rgba(201,168,76,0.15)',
  chromeText: 'rgba(255,255,255,0.9)',
  chromeTextMuted: 'rgba(255,255,255,0.5)',
  chromeTextSubtle: 'rgba(255,255,255,0.7)',
  chromeHover: 'rgba(255,255,255,0.1)',

  // Page surfaces
  pageBg: '#f0f4f8',
  pageBgDark: '#0D1E35',
  cardBg: '#ffffff',
  cardBgDark: 'rgba(255,255,255,0.06)',

  // Text
  textPrimary: '#1E3A5F',
  textSecondary: '#888',
  textOnDark: 'white',
  textOnDarkMuted: 'rgba(255,255,255,0.45)',

  // Buttons / actions (legacy mapping — primaryColor stays the action color)
  primaryButton: '#1E3A5F',
  primaryButtonHover: '#2E6DA4',
  goldButton: '#C9A84C',
  goldButtonHover: '#e0be6a',

  // Semantic
  success: '#27AE60',
  successBg: '#E9F7EF',
  successText: '#1E6B3C',
  warning: '#B7950B',
  warningBg: '#FEF9E7',
  error: '#922B21',
  errorBg: '#FDEDEC',
  link: '#1976d2',
};

// Map legacy short-name fields (primaryColor/secondaryColor/accentColor) onto
// the full palette so existing branding docs continue to work without migration.
// Precedence (high → low):
//   1. Explicit advanced field on the doc (e.g. branding.chromeBg)
//   2. Legacy field mapping (primaryColor → chromeBg etc.)
//   3. DEFAULTS
function mergeLegacy(branding) {
  if (!branding || typeof branding !== 'object') return DEFAULTS;
  // Build the legacy-derived layer first so explicit fields can override it.
  const legacy = {};
  if (branding.primaryColor) {
    legacy.chromeBg = branding.primaryColor;
    legacy.primaryButton = branding.primaryColor;
    legacy.textPrimary = branding.primaryColor;
  }
  if (branding.secondaryColor) {
    legacy.primaryButtonHover = branding.secondaryColor;
  }
  if (branding.accentColor) {
    legacy.chromeAccent = branding.accentColor;
    legacy.goldButton = branding.accentColor;
  }
  // DEFAULTS first, then legacy-derived, then explicit branding fields.
  // Empty strings on branding are treated as "fall back to defaults" — strip them.
  const explicit = {};
  for (const [k, v] of Object.entries(branding)) {
    if (v !== '' && v !== null && v !== undefined) explicit[k] = v;
  }
  return { ...DEFAULTS, ...legacy, ...explicit };
}

const BrandingContext = createContext(DEFAULTS);

export function BrandingProvider({ children }) {
  // Initialize from cache (instant render) — fall back to defaults
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) return mergeLegacy(JSON.parse(cached));
    } catch (e) { /* ignore */ }
    return DEFAULTS;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'branding'));
        if (cancelled || !snap.exists()) return;
        const next = mergeLegacy(snap.data());
        setBranding(next);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snap.data())); } catch (e) { /* ignore */ }
      } catch (e) {
        console.warn('Branding load failed; using cached/defaults:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply favicon dynamically when branding loads
  useEffect(() => {
    if (!branding.faviconUrl) return;
    const link = document.querySelector("link[rel='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = branding.faviconUrl;
    if (!link.parentNode) document.head.appendChild(link);
  }, [branding.faviconUrl]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export const BRANDING_DEFAULTS = DEFAULTS;
